<template>
  <!-- props description https://tiptap.dev/docs/editor/extensions/functionality/bubble-menu#settings -->
  <BubbleMenu
    v-if="editor"
    :editor="editor"
    :plugin-key="`imageBlockMenu-${menuKey}`"
    :should-show="shouldShow"
    :tippy-options="tippyOptions"
    :update-delay="0"
  >
    <div class="ibm-Toolbar">
      <DescriptionButton
        v-tippy="{
          placement: 'top',
          content: $t('action.align_img_left')
        }"
        :active="isActiveImageAlignment(LEFT)"
        icon="align-left"
        icon-height="20"
        icon-width="20"
        @click="onAlignImageChange(LEFT)"
      />
      <DescriptionButton
        v-tippy="{
          placement: 'top',
          content: $t('action.align_img_center')
        }"
        :active="isActiveImageAlignment(CENTER)"
        icon="align-center"
        icon-height="20"
        icon-width="20"
        @click="onAlignImageChange(CENTER)"
      />
      <DescriptionButton
        v-tippy="{
          placement: 'top',
          content: $t('action.align_img_right')
        }"
        :active="isActiveImageAlignment(RIGHT)"
        icon="align-right"
        icon-height="20"
        icon-width="20"
        @click="onAlignImageChange(RIGHT)"
      />
      <AppDivider vertical />
      <AppInputRange
        :max="100"
        :min="25"
        :model-value="width"
        :step="25"
        track-width="100px"
        @update:model-value="onWidthChange"
      >
        {{ width }}%
      </AppInputRange>
    </div>
  </BubbleMenu>
</template>

<script setup>
import { BubbleMenu } from '@tiptap/vue-3'
import { sticky } from 'tippy.js'
import { computed, ref } from 'vue'

import { DROP_LIST_THEMES } from '@/utils/components-configurations/app-droplist'
import { uid } from '@/utils/uid'

import DescriptionButton from '@/components/objectives/forms/DescriptionButton'
import AppDivider from '@/components/ui/AppDivider'
import AppInputRange from '@/components/ui/AppInputRange/AppInputRange'

const props = defineProps({
  editor: {
    type: Object,
    required: true
  },

  appendTo: {
    type: Object,
    default: () => ({})
  },

  active: {
    type: Boolean
  }
})

const menuKey = uid()

const tippyInstance = ref(null)

const shouldShow = () => {
  const node = props.editor.state.selection.$anchor.nodeAfter
  const loadFailed = node?.attrs['data-load-failed'] || false
  return props.editor.isActive('imageBlock') && !loadFailed && props.active
}

const LEFT = 'left'
const CENTER = 'center'
const RIGHT = 'right'

const onAlignImageChange = align => {
  props.editor.chain().focus(null).setImageBlockAlign(align).run()
}

const onWidthChange = value => {
  props.editor.chain().focus(null).setImageBlockWidth(value).run()
}

const isActiveImageAlignment = align => {
  return props.editor.isActive('imageBlock', { align })
}

const width = computed(() => {
  return parseInt(props.editor.getAttributes('imageBlock')?.width || 0)
})

const tippyOptions = {
  offset: [0, 8],
  arrow: false,
  theme: `${DROP_LIST_THEMES.COMMON_THEMES}`,
  popperOptions: {
    modifiers: [{ name: 'flip', enabled: false }] // модификаторы для поведения tippy, отключаем автоматическое перемещение вверх или вниз.
  },
  onCreate(instance) {
    tippyInstance.value = instance
  },
  appendTo: () => {
    return props.appendTo?.current || props.appendTo?.value || document.body
  },
  plugins: [sticky],
  sticky: 'popper'
}
</script>

<style lang="scss">
.ibm-Toolbar {
  display: flex;
  align-items: center;
  align-content: center;
  gap: 8px;
  padding: 12px;
  background: $white;
}
</style>
